import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Space, Card, Typography, Spin } from 'antd';
import { CloseOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import useCustomer from 'hooks/useCustomer';

const { Text } = Typography;

const CITY_COORDS = {
	0: { lat: 43.4987, lon: 43.6171 },
	1: { lat: 55.7558, lon: 37.6173 },
};

// Функция расчета расстояния между двумя точками (формула Гаверсинуса)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
	const R = 6371; // Радиус Земли в км
	const dLat = ((lat2 - lat1) * Math.PI) / 180;
	const dLon = ((lon2 - lon1) * Math.PI) / 180;
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLon / 2) *
			Math.sin(dLon / 2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
};

const getNearestCity = (userLat, userLon) => {
	let nearestCityId = 0;
	let minDistance = Infinity;

	Object.entries(CITY_COORDS).forEach(([id, city]) => {
		const distance = calculateDistance(userLat, userLon, city.lat, city.lon);
		if (distance < minDistance) {
			minDistance = distance;
			nearestCityId = parseInt(id);
		}
	});

	return nearestCityId;
};

const CityConfirm = ({ onClose }) => {
	const { t } = useTranslation();
	const { customer, setCustomer } = useCustomer();
	const location = useLocation();

	const CITIES = {
		0: { name: t('city.nalchik') },
		1: { name: t('city.moscow') },
	};
	const [suggestedCityId, setSuggestedCityId] = useState(null);
	const [loading, setLoading] = useState(true);

	const applyCityAndRedirect = newStoreId => {
		setCustomer({ ...customer, store_id: newStoreId });
		localStorage.setItem('cityConfirmed', 'true');
		const path = location.pathname;
		if (path === '/page/15' && newStoreId === 1) {
			window.location.href = '/page/16';
			return;
		}
		if (path === '/page/16' && newStoreId === 0) {
			window.location.href = '/page/15';
			return;
		}
		window.location.reload();
	};

	useEffect(() => {
		// Попытка определить город через геолокацию
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				position => {
					const { latitude, longitude } = position.coords;
					const nearestCity = getNearestCity(latitude, longitude);
					setSuggestedCityId(nearestCity);
					setLoading(false);
				},
				error => {
					// Если геолокация не удалась, используем текущий город или Нальчик по умолчанию
					console.log('Geolocation error:', error);
					setSuggestedCityId(customer.store_id || 0);
					setLoading(false);
				}
			);
		} else {
			// Браузер не поддерживает геолокацию
			setSuggestedCityId(customer.store_id || 0);
			setLoading(false);
		}
	}, [customer.store_id]);

	const handleConfirm = () => {
		if (customer.store_id === suggestedCityId) {
			localStorage.setItem('cityConfirmed', 'true');
			onClose();
		} else {
			applyCityAndRedirect(suggestedCityId);
		}
	};

	const handleReject = () => {
		const otherCityId = suggestedCityId === 0 ? 1 : 0;
		applyCityAndRedirect(otherCityId);
	};

	const handleClose = () => {
		// Просто закрываем без сохранения выбора (покажется при следующем посещении)
		onClose();
	};

	if (loading) {
		return (
			<Card
				size='small'
				style={{
					width: 320,
					boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
				}}
			>
				<div style={{ textAlign: 'center', padding: '10px 0' }}>
					<Spin />
					<div style={{ marginTop: 10 }}>
						<Text type='secondary'>{t('city.detectingLocation')}</Text>
					</div>
				</div>
			</Card>
		);
	}

	return (
		<Card
			size='small'
			style={{
				width: 320,
				boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
			}}
			extra={
				<Button
					type='text'
					size='small'
					icon={<CloseOutlined />}
					data-click-key='city:close'
					onClick={handleClose}
				/>
			}
		>
			<Space direction='vertical' size='middle' style={{ width: '100%' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<EnvironmentOutlined style={{ fontSize: 18, color: '#1890ff' }} />
					<Text strong style={{ fontSize: 15 }}>
						{t('city.isYourCity', { city: CITIES[suggestedCityId].name })}
					</Text>
				</div>
				<Space size='small' style={{ width: '100%' }}>
					<Button type='primary' data-click-key='city:yes' onClick={handleConfirm} style={{ flex: 1 }}>
						{t('city.yes')}
					</Button>
					<Button data-click-key='city:no' onClick={handleReject} style={{ flex: 1 }}>
						{t('city.no')}
					</Button>
				</Space>
			</Space>
		</Card>
	);
};

export default CityConfirm;
